import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ConfirmPurchaseDialog } from "../components/ConfirmPurchaseDialog";
import { PurchasesCreateFormController } from "../components/PurchasesCreateFormController";
import { PurchasesEditFormController } from "../components/PurchasesEditFormController";
import { PurchasesTable } from "../components/PurchasesTable";
import { DEFAULT_VALUES } from "../components/usePurchasesForm";
import {
	PURCHASES_QUERY_KEY,
	usePurchasesQuery,
} from "../hooks/usePurchasesQuery";
import type {
	MaterialOption,
	Purchase,
	PurchaseFormValues,
	PurchaseResource,
} from "../types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import stellar from "@/services/stellar";

const hasContainsRequests = (
	value: unknown,
): value is { containsRequests?: boolean | null } =>
	typeof value === "object" && value !== null && "containsRequests" in value;

export function PurchasesListPage() {
	const queryClient = useQueryClient();
	const { data: purchasesData, isPending } = usePurchasesQuery();
	const purchases = purchasesData?.purchases ?? [];
	const isLoading = isPending && purchases.length === 0;

	const [isCreationOpen, setIsCreationOpen] = useState(false);
	const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
	const [confirmingPurchase, setConfirmingPurchase] = useState<Purchase | null>(
		null,
	);
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [cancelingPurchase, setCancelingPurchase] = useState<Purchase | null>(
		null,
	);
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
	const [isCanceling, setIsCanceling] = useState(false);

	const handleCloseCreation = useCallback(() => {
		setIsCreationOpen(false);
	}, []);

	const handleCloseEdit = useCallback(() => {
		setEditingPurchase(null);
	}, []);

	const handleCancelRequest = useCallback((purchase: Purchase) => {
		setCancelingPurchase(purchase);
		setIsCancelDialogOpen(true);
	}, []);
	const handleConfirmRequest = useCallback((purchase: Purchase) => {
		setConfirmingPurchase(purchase);
		setIsConfirmDialogOpen(true);
	}, []);
	const handleRequestNewSupplier = useCallback(() => {}, []);
	const handleRequestNewMaterial = useCallback(() => {}, []);
	const handlePurchaseCreated = useCallback(
		(_purchase: PurchaseResource) => {
			queryClient.invalidateQueries({ queryKey: PURCHASES_QUERY_KEY });
		},
		[queryClient],
	);
	const handlePurchaseUpdated = useCallback(
		(_purchase: PurchaseResource) => {
			queryClient.invalidateQueries({ queryKey: PURCHASES_QUERY_KEY });
		},
		[queryClient],
	);

	const handleConfirmDialogChange = useCallback((open: boolean) => {
		setIsConfirmDialogOpen(open);

		if (!open) {
			setConfirmingPurchase(null);
		}
	}, []);

	const handleCancelDialogChange = useCallback((open: boolean) => {
		setIsCancelDialogOpen(open);

		if (!open) {
			setCancelingPurchase(null);
		}
	}, []);

	const handlePurchaseConfirmed = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: PURCHASES_QUERY_KEY });
	}, [queryClient]);

	const handleCancelConfirm = useCallback(async () => {
		if (!cancelingPurchase) {
			return;
		}

		setIsCanceling(true);

		try {
			const response = await stellar.action("removePurchase", {
				id: cancelingPurchase.id,
			});
			const containsRequests =
				hasContainsRequests(response) && Boolean(response.containsRequests);

			if (containsRequests) {
				toast.success("Material voltou para os pedidos.");
			}

			toast.success("Compra cancelada.");
			queryClient.invalidateQueries({ queryKey: PURCHASES_QUERY_KEY });
		} catch {
			toast.error("Não foi possível cancelar a compra. Tente novamente.");
		} finally {
			setIsCanceling(false);
			setIsCancelDialogOpen(false);
			setCancelingPurchase(null);
		}
	}, [cancelingPurchase, queryClient]);

	const resolveOrigin = (
		origin: Purchase["origin"],
	): PurchaseFormValues["origin"] => {
		if (origin === "europe" || origin === "other") {
			return origin;
		}

		return "other";
	};

	const buildMaterialLabel = (material: Purchase["material"]) => {
		const parts = [material.brand, material.model, material.description]
			.map((value) => value.trim())
			.filter((value) => value.length > 0);

		return parts.join(" ");
	};

	const editingFormValues = editingPurchase
		? {
				...DEFAULT_VALUES,
				id: editingPurchase.id,
				supplier: editingPurchase.supplier?.id ?? "",
				material: editingPurchase.material.id ?? "",
				origin: resolveOrigin(editingPurchase.origin),
				price:
					editingPurchase.price ??
					editingPurchase.material.price ??
					DEFAULT_VALUES.price,
				quantity: editingPurchase.quantity,
				link: editingPurchase.link ?? "",
				observation: "",
				request: "",
			}
		: null;

	const editingSupplierOption = editingPurchase?.supplier
		? {
				id: editingPurchase.supplier.id,
				name: editingPurchase.supplier.name,
			}
		: null;

	const editingMaterialOption: MaterialOption | null = editingPurchase?.material
		.id
		? {
				id: editingPurchase.material.id,
				label: buildMaterialLabel(editingPurchase.material) || "Material",
			}
		: null;

	return (
		<div className="space-y-6 p-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="space-y-1">
					<h1 className="text-lg font-semibold leading-6 text-foreground">
						Lista de compras
					</h1>
					<p className="text-sm text-muted-foreground">
						Acompanhe compras em aberto e previsões de entrega.
					</p>
				</div>
				<Button size="sm" onClick={() => setIsCreationOpen(true)}>
					Criar compra
				</Button>
			</div>

			<PurchasesTable
				purchases={purchases}
				isLoading={isLoading}
				onConfirm={handleConfirmRequest}
				onEdit={setEditingPurchase}
				onCancel={handleCancelRequest}
			/>

			{isCreationOpen ? (
				<PurchasesCreateFormController
					open={isCreationOpen}
					defaultValues={DEFAULT_VALUES}
					disableMaterial={false}
					supplierOption={null}
					materialOption={null}
					onRequestNewSupplier={handleRequestNewSupplier}
					onRequestNewMaterial={handleRequestNewMaterial}
					onClose={handleCloseCreation}
					onCancel={handleCloseCreation}
					onCreated={handlePurchaseCreated}
				/>
			) : null}

			{editingFormValues ? (
				<PurchasesEditFormController
					open={Boolean(editingFormValues)}
					defaultValues={editingFormValues}
					disableMaterial={false}
					supplierOption={editingSupplierOption}
					materialOption={editingMaterialOption}
					onRequestNewSupplier={handleRequestNewSupplier}
					onRequestNewMaterial={handleRequestNewMaterial}
					onClose={handleCloseEdit}
					onCancel={handleCloseEdit}
					onUpdated={handlePurchaseUpdated}
				/>
			) : null}

			<AlertDialog
				open={isCancelDialogOpen}
				onOpenChange={handleCancelDialogChange}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancelar compra</AlertDialogTitle>
						<AlertDialogDescription>
							Tem a certeza que pretende cancelar a compra?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isCanceling}>
							Cancelar
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-white hover:bg-destructive/90"
							onClick={handleCancelConfirm}
							disabled={!cancelingPurchase || isCanceling}
							aria-busy={isCanceling ? "true" : undefined}
						>
							{isCanceling ? "A cancelar..." : "Continuar"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<ConfirmPurchaseDialog
				purchase={confirmingPurchase}
				open={isConfirmDialogOpen}
				onOpenChange={handleConfirmDialogChange}
				onConfirmed={handlePurchaseConfirmed}
			/>
		</div>
	);
}
